import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

// Ephemeral chrome/UI state, shared to avoid prop drilling: the mobile drawer, which nav
// branches are expanded, and the toast notification.
// The URL owns navigation position — this owns transient UI only.

export type NotificationVariant = 'success' | 'error';

export interface Notification {
    message: string;
    variant: NotificationVariant;
}

export interface UIContextValue {
    // Mobile nav drawer
    drawerOpen: boolean;
    openDrawer: () => void;
    closeDrawer: () => void;
    toggleDrawer: () => void;
    // Nav tree branch expansion, keyed by the branch's slug path (e.g. 'paintings/series')
    expanded: Record<string, boolean>;
    isExpanded: (key: string) => boolean;
    toggleExpanded: (key: string) => void;
    setExpanded: (key: string, open: boolean) => void;
    // Toast notification (auto-dismisses)
    notification: Notification | null;
    notify: (n: Notification) => void;
    dismissNotification: () => void;
}

const NOTIFICATION_TIMEOUT_MS = 3500; // preserves the current contact-form auto-dismiss timing

const UIContext = createContext<UIContextValue | null>(null);

export const UIProvider = ({ children }: { children: React.ReactNode }) => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [expanded, setExpandedState] = useState<Record<string, boolean>>({});
    const [notification, setNotification] = useState<Notification | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout>>();

    const openDrawer = useCallback(() => setDrawerOpen(true), []);
    const closeDrawer = useCallback(() => setDrawerOpen(false), []);
    const toggleDrawer = useCallback(() => setDrawerOpen((open) => !open), []);

    const isExpanded = useCallback((key: string) => Boolean(expanded[key]), [expanded]);
    const toggleExpanded = useCallback(
        (key: string) => setExpandedState((prev) => ({ ...prev, [key]: !prev[key] })),
        [],
    );
    const setExpanded = useCallback(
        (key: string, open: boolean) => setExpandedState((prev) => ({ ...prev, [key]: open })),
        [],
    );

    const dismissNotification = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        setNotification(null);
    }, []);

    const notify = useCallback((n: Notification) => {
        if (timerRef.current) clearTimeout(timerRef.current);
        setNotification(n);
        timerRef.current = setTimeout(() => setNotification(null), NOTIFICATION_TIMEOUT_MS);
    }, []);

    // Clear any pending timer on unmount.
    useEffect(() => () => {
        if (timerRef.current) clearTimeout(timerRef.current);
    }, []);

    const value = useMemo<UIContextValue>(
        () => ({
            drawerOpen,
            openDrawer,
            closeDrawer,
            toggleDrawer,
            expanded,
            isExpanded,
            toggleExpanded,
            setExpanded,
            notification,
            notify,
            dismissNotification,
        }),
        [
            drawerOpen,
            openDrawer,
            closeDrawer,
            toggleDrawer,
            expanded,
            isExpanded,
            toggleExpanded,
            setExpanded,
            notification,
            notify,
            dismissNotification,
        ],
    );

    return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
};

export const useUI = (): UIContextValue => {
    const ctx = useContext(UIContext);
    if (!ctx) throw new Error('useUI must be used within a UIProvider');
    return ctx;
};
